import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../firebase/auth.tsx";
import "../styles/main.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTranslation } from "react-i18next";
import { getTranslatedText } from "./helpers/translationMap";
import { getTrainingsHistory } from "./helpers/requests.tsx";
import { useTableFilters } from "./hooks/useTableFilters.tsx";
import TableHeaderFilter from "./common/TableHeaderFilter.tsx";

const Main: React.FC = () => {
  const { userId, language } = useAuth();

  const [trainingHistory, setTrainingHistory] = useState<any[]>([]);
  // filteredTrainingHistory is now handled by the hook
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [adminView, setAdminView] = useState(false);
  const [headquarterView, setHeadquarterView] = useState(false);
  const [headsetView, setHeadsetView] = useState(false); // Renamed from groupView
  
  const [baseColumnMultiplicator, setBaseColumnMultiplicator] = useState(7);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filter hook
  const {
    filteredData: filteredTrainingHistory,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters
  } = useTableFilters(trainingHistory);

  // Pagination logic applied to the filtered data
  const paginatedData = filteredTrainingHistory.slice(0, currentPage * 100);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const trainingsData = await getTrainingsHistory(userId);
        if (!trainingsData || trainingsData.length === 0) {
          setTrainingHistory([]);
        } else {
          setTrainingHistory(trainingsData.history);

          switch (trainingsData.view) {
            case "admin":
              setAdminView(true);
              setBaseColumnMultiplicator(10);
              break;
            case "headquarterAdmin":
              setHeadquarterView(true);
              setBaseColumnMultiplicator(9);
              break;
            case "headsetAdmin": // Updated role name
              setHeadsetView(true); // Updated state name
              setBaseColumnMultiplicator(8);
              break;
          }
        }
      } catch (error) {
        console.error("Error getting trainings history:", error);
      }
      setLoading(false);
    };

    if (userId) {
      fetchData();
    }    
  }, [userId]);

  const handleRowClick = (index: number) => {
    setSelectedRow(paginatedData[index]);
  };

  const getLocalizedResult = (value) => {
    if (typeof value !== "string") {
      return value || "";
    }

    const match = value.match(/Failed\s\((\d+)\/(\d+)\)/);
    if (match) {
      const current = match[1];
      const total = match[2];
      return t("FailedWithNumbers", { current, total });
    }
    return t(value);
  };

  const renderTrainingDetailsModal = () => {
    if (!selectedRow) return null;

    let trainingResultObject = null;

    if (selectedRow.trainingResult) {
      try {
        trainingResultObject = JSON.parse(selectedRow.trainingResult);
      } catch (error) {
        console.error("Invalid JSON format:", error);
        trainingResultObject = null;
      }
    }

    return (
      <div className="confirmation-modal">
        <div className="modal-content">
          <p>{t("Training details")}</p>
          <br />
          <div>
            {t("Device name")}:{" "}
            <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
              {selectedRow.trainingName}
            </span>
          </div>
          <div>
            {t("Training type")}:{" "}
            <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
              {t(selectedRow.trainingType)}
            </span>
          </div>
          <div>
            {t("Start_time")}:{" "}
            <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
              {formatFirestoreTimestamp(selectedRow.trainingTime)}
            </span>
          </div>
          <div>
            {t("Duration")}:{" "}
            <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
              {selectedRow.trainingDuration}
            </span>
          </div>
          <div>
            {t("Mode")}:{" "}
            <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
              {t(selectedRow.trainingMode)}
            </span>
          </div>
          <div>
            {t("Result")}:{" "}
            <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
              {getLocalizedResult(selectedRow.trainingResultValue)}
            </span>
          </div>
          {trainingResultObject &&
            trainingResultObject.results &&
            trainingResultObject.results.length > 0 &&
            trainingResultObject.results[0].text !== "noCounter" && (
              <>
                <br />
                <div>
                  {t("Details")}:
                  <ul>
                    {trainingResultObject.results.map((result, index) => (
                      <li
                        key={index}
                        style={{ color: result.result ? "#44963C" : "#A63939" }}
                      >
                        {getTranslatedText(result.text, t)}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

          <div className="button-container">
            <button
              type="button"
              className="buttonCancel btn btn-primary"
              onClick={() => setSelectedRow(null)}
            >
              {t("Close")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="main-container">
      {loading ? (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "0.7rem",
          }}
        >
          {t("Loading...")}
        </div>
      ) : (
        <>
          <div
            style={{
              marginTop: "80px",
              marginLeft: "50px",
              marginRight: "40px",
              height: "100%",
              minHeight: "calc(100vh - 120px)",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#39A686 transparent",
            }}
          >
            {/* Separate table header - don't find other way to avoid overlapping header on scroll */}
            <div
              style={{
                position: "sticky",
                top: "0",
                zIndex: "1",
                backgroundColor: "transparent",
                border: "none",
                marginLeft: "12px",
                marginRight: "11px",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <table
                className="table"
                style={{
                  borderColor: "transparent",
                  marginBottom: "0px",
                  tableLayout: "fixed",
                  color: "#39A686",
                }}
              >
                <thead>
                  <tr>
                    {(adminView || headquarterView || headsetView) && ( // Updated check
                      <th
                        scope="col"
                        style={{
                          width: `calc(100% / ${baseColumnMultiplicator})`,
                          fontWeight: "normal",
                        }}
                      >
                        <TableHeaderFilter
                          column="firstName"
                          title={t("User")}
                          data={trainingHistory}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                    )}
                    {adminView && (
                      <th
                        scope="col"
                        style={{
                          width: `calc(100% / ${baseColumnMultiplicator - 1})`,
                          fontWeight: "normal",
                        }}
                      >
                        <TableHeaderFilter
                          column="headquarterName"
                          title={t("Headquarter")}
                          data={trainingHistory}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                    )}
                    {(adminView || headquarterView) && (
                      <th
                        scope="col"
                        style={{
                          width: `calc(100% / ${baseColumnMultiplicator - 1})`,
                          fontWeight: "normal",
                        }}
                      >
                        <TableHeaderFilter
                          column="groupName"
                          title={t("Headset")}
                          data={trainingHistory}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                    )}
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator - 1})`,
                        fontWeight: "normal",
                      }}
                    >
                      <TableHeaderFilter
                        column="trainingName"
                        title={t("Device_name")}
                        data={trainingHistory}
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                      />
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator - 3})`,
                        fontWeight: "normal",
                      }}
                    >
                      <TableHeaderFilter
                        column="trainingType"
                        title={t("Training_type")}
                        data={trainingHistory}
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                        getLocalizedValue={(val) => t(val)}
                      />
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator - 3})`,
                        fontWeight: "normal",
                      }}
                    >
                      {t("Start_time")}
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator + 4})`,
                        fontWeight: "normal",
                      }}
                    >
                      {t("Duration")}
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator + 4})`,
                        fontWeight: "normal",
                      }}
                    >
                      <TableHeaderFilter
                        column="trainingMode"
                        title={t("Mode")}
                        data={trainingHistory}
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                        getLocalizedValue={(val) => t(val)}
                      />
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator + 4})`,
                        fontWeight: "normal",
                      }}
                    >
                      <TableHeaderFilter
                        column="trainingResultValue" // Using the correct key from data
                        title={t("Result")}
                        data={trainingHistory}
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                        getLocalizedValue={getLocalizedResult} // Use custom localizer for Result
                        lastColumn={true}
                      />
                    </th>
                    <th className="detailsButtonWrapper"></th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Table with restricted height and scrollbar */}
            <div
              style={{
                maxHeight: "calc(100vh - 165px)",
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#39A686 transparent",
              }}
            >
              <div className="container-fluid" style={{ paddingRight: "0px" }}>
                <table
                  className="table"
                  style={{
                    tableLayout: "fixed",
                  }}
                >
                  <tbody>
                    {paginatedData.map(
                      (trainingRecord: any, index: number) => (
                        <tr
                          key={`${formatFirestoreTimestamp(
                            trainingRecord.trainingTime
                          )}-${trainingRecord.trainingId}-${index}`}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#D4E5E2" : "transparent",
                          }}
                        >
                          {(adminView || headquarterView || headsetView) && ( // Updated check
                            <td
                              style={{
                                width: `calc(100% / ${baseColumnMultiplicator})`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                              }}
                            >
                              <div className="containerHeader">
                                <img
                                  src="/images/Icon_User_Simple.png"
                                  style={{
                                    width: "2vh",
                                    minWidth: "10px",
                                    marginLeft: "0.3vh",
                                    marginRight: "1.5vh",
                                  }}
                                />
                                <div className="userName">
                                  {trainingRecord.firstName}
                                  <br />
                                  {trainingRecord.lastName}
                                </div>
                              </div>
                            </td>
                          )}
                          {adminView && (
                            <td
                              style={{
                                width: `calc(100% / ${
                                  baseColumnMultiplicator - 1
                                })`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                              }}
                            >
                              {trainingRecord.headquarterName}
                            </td>
                          )}
                          {(adminView || headquarterView) && (
                            <td
                              style={{
                                width: `calc(100% / ${
                                  baseColumnMultiplicator - 1
                                })`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                                lineHeight: 1,
                              }}
                            >
                              {trainingRecord.headsetName || trainingRecord.groupName}
                              {(trainingRecord.headsetName ||
                                trainingRecord.groupName) && (
                                <span
                                  style={{
                                    color: "#39a686",
                                  }}
                                >
                                  &nbsp;|{" "}
                                  {t("country_" + trainingRecord.countryCode)}
                                </span>
                              )}
                            </td>
                          )}
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator - 1
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {trainingRecord.trainingName}
                          </td>
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator - 3
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {t(trainingRecord.trainingType)}
                          </td>
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator - 3
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {formatFirestoreTimestamp(
                              trainingRecord.trainingTime
                            )}
                          </td>
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator + 4
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {trainingRecord.trainingDuration}
                          </td>
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator + 4
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {t(trainingRecord.trainingMode)}
                          </td>
                          <td
                            style={{
                              color: trainingRecord.totalResult
                                ? "#44963C"
                                : "#A63939",
                              width: `calc(100% / ${
                                baseColumnMultiplicator + 4
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {getLocalizedResult(
                              trainingRecord.trainingResultValue
                            )}
                          </td>
                          <td className="detailsButtonWrapper">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => handleRowClick(index)}
                            >
                              {t("Details")}
                            </button>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
                {currentPage * 100 < filteredTrainingHistory.length && (
                  <div style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      style={{
                        backgroundColor: "#39A686",
                        borderColor: "#39A686",
                        borderRadius: "10px",
                        marginRight: "10px",
                        marginBottom: "10px",
                        fontSize: "1em",
                      }}
                    >
                      {t("Show_More")}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {selectedRow && renderTrainingDetailsModal()}
          </div>
        </>
      )}{" "}
    </div>
  );
};

const formatFirestoreTimestamp = (timestamp: any): string => {
  const seconds = timestamp?._seconds ?? timestamp?.seconds;
  if (typeof seconds === "number") {
    const date = new Date(seconds * 1000);

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };
    return date.toLocaleString("en-GB", options);
  }

  if (timestamp && typeof timestamp.toDate === "function") {
    const date = timestamp.toDate();
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };
    return date.toLocaleString("en-GB", options);
  }

  if (typeof timestamp === "string") {
    return timestamp;
  }

  return "Invalid Date";
};

export default Main;